import React from "react";
import { Loader } from "semantic-ui-react";

import { resolveGradingScheme } from "../../utils/gradingScheme";
import CardLegacy4Q from "./cards/CardLegacy4Q";
import CardNumeric3Term from "./cards/CardNumeric3Term";
import CardDescriptive from "./cards/CardDescriptive";
import CardSHS11 from "./cards/CardSHS11";
import CardSHS12 from "./cards/CardSHS12";

/**
 * Picks which report card to render.
 *
 * The card used to be one 1275-line component with four quarters written into
 * every average, column header and render gate. It now lives in cards/, one
 * file per variant, chosen by the grading scheme:
 *
 *   legacy-4q      school years before 2026-2027, rendered by the original
 *                  component, moved verbatim and frozen
 *   numeric-3term  Grades 2-12 from SY 2026-2027
 *   descriptive    Kinder and Grade 1 from SY 2026-2027, letters not numbers
 *   shs-2table     Grade 12 on the 2016 curriculum - now one whole-year table
 *
 * Routing legacy years to untouched code is the regression guarantee: nothing
 * in the new variants can change how a historical card prints.
 *
 * Strengthened SHS Grade 11 is the one split the scheme does not name. It
 * resolves to numeric-3term and is then steered here to its own card, because
 * cardVariant is asserted by utils/__fixtures__/gradingScheme.vectors.json,
 * which sis-backend/test/gradingScheme.check.js asserts against its own copy of
 * the resolver. Adding a variant means a synchronised change in both repos for
 * a token only this file reads. If that sync happens for another reason, fold
 * this branch back into the scheme where it belongs.
 */

// DM 012, s. 2026. Grade 12's strand types are deliberately not here.
const STRENGTHENED_SHS = ["SHSACAD", "SHSTECHPRO"];

const FacsimileDetailsPV = (props) => {
  const { schoolYear } = props;

  // Take the section off the student, not off props.section: the container
  // initialises that to [] and never fills it, and [] is truthy, so reading it
  // gave every card an undefined gradeLevel and routed the lot to the numeric
  // variant - Grade 1 printing numbers instead of letters. Every card
  // component already reads sectionStudent.section, which is the populated one.
  const section =
    (props.sectionStudent && props.sectionStudent.section) ||
    (Array.isArray(props.section) ? props.section[0] : props.section);

  // The scheme decides everything downstream, so nothing renders until the
  // school year has arrived. Guessing from an id is not an option: a uuid can
  // parse as a plausible year and silently pick the wrong number of terms.
  if (!schoolYear || !section) return <Loader active inline="centered" />;

  const scheme = resolveGradingScheme(schoolYear, section.gradeLevel, {
    levelType: section.levelType,
  });

  switch (scheme.cardVariant) {
    case "descriptive":
      return <CardDescriptive {...props} scheme={scheme} />;

    case "numeric-3term":
      return STRENGTHENED_SHS.indexOf(section.levelType) !== -1 ? (
        <CardSHS11 {...props} scheme={scheme} />
      ) : (
        <CardNumeric3Term {...props} scheme={scheme} />
      );

    // The variant is still named for the two semester tables it used to mean.
    // Grade 12 runs its 2016-curriculum subjects across the whole year from SY
    // 2026-2027, so there is nothing left to split in two; the name stays
    // because sis-backend asserts it against the shared resolver fixture.
    case "shs-2table":
      return <CardSHS12 {...props} scheme={scheme} />;

    case "legacy-4q":
    default:
      return <CardLegacy4Q {...props} />;
  }
};

export default FacsimileDetailsPV;
